import { AlertCircle, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ReasoningEffort } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { useTeamAllowedModels } from "@/features/teams/hooks/useTeamAllowedModels";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
}

// "default" no select = usar default_reasoning_effort do modelo (null).
const REASONING_USE_DEFAULT = "__use_default__" as const;

export function ModelTab({ draft, onChange }: Props) {
  const { t } = useTranslation("agents");
  const allowedQuery = useTeamAllowedModels(draft.owner_team_id || "");

  const allowed = allowedQuery.data ?? [];
  const selectedNotInWhitelist =
    draft.llm_model_id !== "" &&
    !allowed.some((a) => a.llm_model_id === draft.llm_model_id);

  const selectedModel = allowed.find(
    (a) => a.llm_model_id === draft.llm_model_id,
  );
  const showReasoning = selectedModel?.supports_reasoning ?? false;
  const modelDefaultEffort = selectedModel?.default_reasoning_effort ?? null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="agent-llm">{t("model.llmModel")} *</Label>
        <Select
          value={draft.llm_model_id || undefined}
          onValueChange={(v) => onChange({ ...draft, llm_model_id: v })}
          disabled={!draft.owner_team_id}
        >
          <SelectTrigger id="agent-llm">
            <SelectValue placeholder={t("model.llmModelPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {allowed.length === 0 ? (
              <SelectItem value="empty" disabled>
                {t("model.llmModelEmpty")}
              </SelectItem>
            ) : (
              allowed.map((a) => (
                <SelectItem key={a.llm_model_id} value={a.llm_model_id}>
                  {a.display_name}
                  {a.is_default ? t("model.llmModelDefaultSuffix") : ""}
                  {a.supports_reasoning
                    ? t("model.llmModelReasoningSuffix")
                    : ""}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {!draft.owner_team_id ? (
          <p className="text-xs text-muted-foreground">
            {t("model.llmModelNoTeamHint")}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("model.llmModelHint")}
          </p>
        )}
        {selectedNotInWhitelist ? (
          <Badge
            variant="destructive"
            className="mt-2 inline-flex items-center gap-1"
          >
            <AlertCircle className="h-3 w-3" />
            {t("errors.llmNotWhitelisted")}
          </Badge>
        ) : null}
      </div>

      {showReasoning ? (
        <div className="space-y-2">
          <Label
            htmlFor="agent-reasoning"
            className="flex items-center gap-1.5"
          >
            <Lightbulb className="h-3.5 w-3.5 text-yellow-600" />
            {t("model.reasoningEffort")}
          </Label>
          <Select
            value={draft.reasoning_effort ?? REASONING_USE_DEFAULT}
            onValueChange={(v) =>
              onChange({
                ...draft,
                reasoning_effort:
                  v === REASONING_USE_DEFAULT
                    ? null
                    : (v as ReasoningEffort),
              })
            }
          >
            <SelectTrigger id="agent-reasoning">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={REASONING_USE_DEFAULT}>
                {t("model.reasoningDefault", {
                  default:
                    modelDefaultEffort ?? t("model.reasoningOff"),
                })}
              </SelectItem>
              <SelectItem value="off">
                {t("model.reasoning.off")}
              </SelectItem>
              <SelectItem value="minimal">
                {t("model.reasoning.minimal")}
              </SelectItem>
              <SelectItem value="low">
                {t("model.reasoning.low")}
              </SelectItem>
              <SelectItem value="medium">
                {t("model.reasoning.medium")}
              </SelectItem>
              <SelectItem value="high">
                {t("model.reasoning.high")}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t("model.reasoningHint")}
          </p>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <input
          id="agent-pii"
          type="checkbox"
          checked={draft.pii_filter_enabled}
          onChange={(e) =>
            onChange({ ...draft, pii_filter_enabled: e.target.checked })
          }
        />
        <Label htmlFor="agent-pii">{t("model.piiFilter")}</Label>
      </div>
    </div>
  );
}
